<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import PresencaRelatorioAPI from 'dashboard/api/fandata/presencaRelatorio';

// T-291 · PRESENÇA DO AGENTE. Pedido da Tamara em 02/10: "preciso de uma visão de login/logout
// (horário por dia) por usuário da plataforma e também uma visão que consiga verificar total de
// horas logado, tempo disponível, tempo em atendimento, total de hr em pausa (por tipo), que eu
// consiga filtrar por período".
//
// ⚠️ "Em atendimento" nesta tela é o ESTADO OCUPADO, declarado pela pessoa. Quanto tempo um
// atendimento levou é outra tela (Tempo de atendimento). Ver Fandata::PresencaRelatorio.
const { t } = useI18n();

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const sinceTs = ref(getUnixStartOfDay(customDateRange.value[0]));
const untilTs = ref(getUnixEndOfDay(customDateRange.value[1]));

const data = reactive({ usuarios: [], total: {}, desde: null });
const carregando = ref(false);
// Uma pessoa aberta por vez: a lista por dia é longa e duas abertas juntas já não cabem na tela.
const abertoId = ref(null);

const ESTADOS = ['online', 'busy', 'pre_pausa', 'reuniao'];

// Horas e minutos, não segundos: ninguém lê turno em segundos. Abaixo de um minuto vira "—",
// porque zero minuto de reunião e nenhuma reunião são a mesma notícia.
const fmt = seg => {
  if (!seg) return '—';
  const h = Math.floor(seg / 3600);
  const m = Math.round((seg % 3600) / 60);
  if (!h) return `${m}min`;
  return `${h}h${String(m).padStart(2, '0')}`;
};

const hora = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const dataCurta = dia => {
  const [a, m, d] = dia.split('-');
  return `${d}/${m}/${a.slice(2)}`;
};

const registroDesde = computed(() => {
  if (!data.desde) return null;
  const d = new Date(data.desde * 1000);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
});

// O aviso só aparece quando o período pedido começa ANTES do primeiro registro, que é quando o
// número engana: sem ele, quem olhasse a semana passada veria zero e concluiria que ninguém
// trabalhou, em vez de "não havia medição".
const periodoAntesDoRegistro = computed(
  () => !!data.desde && sinceTs.value < data.desde
);

const counters = computed(() => [
  { key: 'PESSOAS', value: data.total.pessoas ?? 0, accent: 'text-n-slate-12' },
  { key: 'LOGADO', value: fmt(data.total.logado_seg), accent: 'text-n-slate-12' },
  {
    key: 'DISPONIVEL',
    value: fmt(data.total.estados?.online),
    accent: 'text-n-teal-11',
  },
  {
    key: 'PAUSA',
    value: fmt(
      (data.total.estados?.pre_pausa ?? 0) + (data.total.estados?.reuniao ?? 0)
    ),
    accent: 'text-n-amber-11',
  },
]);

const fetchData = async () => {
  carregando.value = true;
  try {
    const { data: res } = await PresencaRelatorioAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
    });
    data.usuarios = res.usuarios || [];
    data.total = res.total || {};
    data.desde = res.desde ?? null;
  } catch (e) {
    useAlert(t('PRESENCA_RELATORIO.ERROR'));
  } finally {
    carregando.value = false;
  }
};

const alternar = linha => {
  abertoId.value = abertoId.value === linha.user_id ? null : linha.user_id;
};

const onDateRangeChange = value => {
  const [start, end, rangeType] = value;
  customDateRange.value = [start, end];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  sinceTs.value = getUnixStartOfDay(start);
  untilTs.value = getUnixEndOfDay(end);
  fetchData();
};

onMounted(fetchData);
</script>

<template>
  <div class="flex flex-col w-full h-full px-6 py-4 overflow-auto">
    <div class="flex flex-col gap-3 mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('PRESENCA_RELATORIO.HEADER') }}
      </h1>
      <p class="-mt-1 text-sm text-n-slate-11">
        {{ t('PRESENCA_RELATORIO.HINT') }}
      </p>
      <p
        v-if="periodoAntesDoRegistro"
        class="flex items-start gap-1.5 -mt-1 text-sm text-n-amber-11"
      >
        <span class="i-lucide-info size-4 shrink-0 mt-0.5" />
        {{ t('PRESENCA_RELATORIO.DESDE', { data: registroDesde }) }}
      </p>
      <div class="flex flex-wrap items-start gap-3">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
        <BaixarRelatorio
          nome="presenca"
          :dados="data"
          :since="sinceTs"
          :until="untilTs"
        />
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 mb-6 md:grid-cols-4">
      <div
        v-for="c in counters"
        :key="c.key"
        class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <span class="text-xs font-medium text-n-slate-11">
          {{ t(`PRESENCA_RELATORIO.COUNTERS.${c.key}`) }}
        </span>
        <span class="text-2xl font-semibold" :class="c.accent">
          {{ c.value }}
        </span>
      </div>
    </div>

    <div class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1">
      <div class="px-5 py-3 border-b border-n-weak">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('PRESENCA_RELATORIO.BY_USER') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('PRESENCA_RELATORIO.BY_USER_HINT') }}
        </p>
      </div>

      <div v-if="carregando" class="px-5 py-10 text-center">
        <p class="text-sm text-n-slate-11">
          {{ t('PRESENCA_RELATORIO.LOADING') }}
        </p>
      </div>
      <div v-else-if="!data.usuarios.length" class="px-5 py-10 text-center">
        <p class="text-sm text-n-slate-11">
          {{ t('PRESENCA_RELATORIO.EMPTY') }}
        </p>
      </div>

      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-n-slate-11">
            <th class="px-5 py-2 font-medium text-left">
              {{ t('PRESENCA_RELATORIO.COL_PESSOA') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('PRESENCA_RELATORIO.COL_LOGADO') }}
            </th>
            <th
              v-for="e in ESTADOS"
              :key="e"
              class="px-3 py-2 font-medium text-center"
            >
              {{ t(`PRESENCA_RELATORIO.ESTADO.${e}`) }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('PRESENCA_RELATORIO.COL_ENTRADA') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('PRESENCA_RELATORIO.COL_SAIDA') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="linha in data.usuarios" :key="linha.user_id">
            <tr
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
              @click="alternar(linha)"
            >
              <td class="px-5 py-2 font-medium text-n-slate-12">
                <span class="inline-flex items-center gap-1.5">
                  <span
                    class="size-3.5 shrink-0 text-n-slate-10"
                    :class="
                      abertoId === linha.user_id
                        ? 'i-lucide-chevron-down'
                        : 'i-lucide-chevron-right'
                    "
                  />
                  {{ linha.nome }}
                </span>
              </td>
              <td
                class="px-3 py-2 font-semibold text-center tabular-nums text-n-slate-12"
              >
                {{ fmt(linha.logado_seg) }}
              </td>
              <td
                v-for="e in ESTADOS"
                :key="e"
                class="px-3 py-2 text-center tabular-nums text-n-slate-11"
              >
                {{ fmt(linha.estados[e]) }}
              </td>
              <td class="px-3 py-2 text-center tabular-nums text-n-slate-11">
                {{ hora(linha.primeira_entrada) }}
              </td>
              <td class="px-3 py-2 text-center tabular-nums text-n-slate-11">
                {{ hora(linha.ultima_saida) }}
              </td>
            </tr>
            <tr v-if="abertoId === linha.user_id" class="bg-n-alpha-1">
              <td colspan="8" class="px-5 py-3">
                <p class="mb-2 text-xs font-medium text-n-slate-11">
                  {{ t('PRESENCA_RELATORIO.POR_DIA') }}
                </p>
                <table class="w-full text-xs">
                  <thead>
                    <tr class="text-n-slate-11">
                      <th class="py-1 font-medium text-left">
                        {{ t('PRESENCA_RELATORIO.COL_DIA') }}
                      </th>
                      <th class="py-1 font-medium text-center">
                        {{ t('PRESENCA_RELATORIO.COL_ENTRADA') }}
                      </th>
                      <th class="py-1 font-medium text-center">
                        {{ t('PRESENCA_RELATORIO.COL_SAIDA') }}
                      </th>
                      <th class="py-1 font-medium text-center">
                        {{ t('PRESENCA_RELATORIO.COL_LOGADO') }}
                      </th>
                      <th
                        v-for="e in ESTADOS"
                        :key="e"
                        class="py-1 font-medium text-center"
                      >
                        {{ t(`PRESENCA_RELATORIO.ESTADO.${e}`) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="d in linha.dias"
                      :key="d.dia"
                      class="border-t border-n-weak"
                    >
                      <td class="py-1 font-medium text-n-slate-12">
                        {{ dataCurta(d.dia) }}
                      </td>
                      <td class="py-1 text-center tabular-nums">
                        {{ hora(d.entrada) }}
                      </td>
                      <td class="py-1 text-center tabular-nums">
                        {{ hora(d.saida) }}
                      </td>
                      <td class="py-1 font-semibold text-center tabular-nums">
                        {{ fmt(d.logado_seg) }}
                      </td>
                      <td
                        v-for="e in ESTADOS"
                        :key="e"
                        class="py-1 text-center tabular-nums text-n-slate-11"
                      >
                        {{ fmt(d.estados[e]) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
